import { ScreenError, ScreenLoading } from '../../components/state'
import { t } from '../../copy'
import { useSecurityReport } from '../../app/queries'
import { cn } from '../../components/cn'
import './sec.css'

/**
 * 安全体检。
 *
 * # 这一屏刻意没有分数
 *
 * 设计稿上那个「92 · 挺健康」需要一个评分模型，而那个模型还没定。
 * 编一个出来**比不做这一屏更危险——它会让人以为体检过了**。
 * 契约（openapi SecurityReport）也把这个字段刻意留掉了。
 *
 * # 「我已经补好了」为什么恒是 0
 *
 * 因为在数据里它还不存在：SecurityCheckState 那一档是新加的，
 * 今天所有结论都是 open。给 0 是实话；编一个数出来会让用户以为
 * 平台替他补过东西。所以这一档**有真数才显示**。
 *
 * # 分档与句子都不在前端算
 *
 * `level`（得管 / 找空补）与那三句话都由后端给。前端自己从 severity 映射、
 * 或者自己拼模板 id，都会变成两份判据——改一处不改另一处时，
 * 界面与接口各说各话。
 */
export function SecScreen() {
  const report = useSecurityReport()

  if (report.isPending) return <ScreenLoading />
  if (report.error) return <ScreenError error={report.error} onRetry={() => report.refetch()} />

  const checks = report.data?.checks ?? []
  const counts = report.data?.counts
  const mustFix = checks.filter((c) => c.level === 'must_fix')
  const whenFree = checks.filter((c) => c.level === 'when_free')
  const neverChecked = !report.data?.checked_at

  return (
    <div className="sec-theme mx-auto w-full max-w-[980px] px-6 pb-16 pt-8 sm:px-10">
      <header className="mb-7">
        <h1 className="text-npc-h1-sm font-semibold tracking-[-0.01em] text-npc-ink">
          {t('sec.page.title')}
        </h1>
        <p className="mt-2 text-npc-body leading-[1.7] text-npc-ink-soft">{t('sec.page.subtitle')}</p>
      </header>

      {neverChecked ? (
        <EmptyCard body={t('sec.empty')} testId="sec-empty" />
      ) : checks.length === 0 ? (
        <EmptyCard body={t('sec.clean')} testId="sec-clean" />
      ) : (
        <>
          <div className="mb-6 flex flex-wrap gap-3">
            <CountChip
              label={t('sec.count.must_fix')}
              value={counts?.must_fix ?? mustFix.length}
              tone="danger"
              testId="sec-count-must_fix"
            />
            <CountChip
              label={t('sec.count.when_free')}
              value={counts?.when_free ?? whenFree.length}
              tone="warn"
              testId="sec-count-when_free"
            />
          </div>
          <div data-testid="sec-checks">
            {mustFix.length > 0 && (
              <Group title={t('sec.level.must_fix')} tone="danger" checks={mustFix} />
            )}
            {whenFree.length > 0 && (
              <Group title={t('sec.level.when_free')} tone="warn" checks={whenFree} />
            )}
          </div>
        </>
      )}
    </div>
  )
}

type Check = NonNullable<ReturnType<typeof useSecurityReport>['data']>['checks'][number]

function EmptyCard({ body, testId }: { body: string; testId: 'sec-empty' | 'sec-clean' }) {
  return (
    <div className="rounded-npc-card border border-npc-border bg-npc-panel px-6 py-8" data-testid={testId}>
      <p className="text-npc-section-sm leading-[1.7] text-npc-ink-medium">{body}</p>
    </div>
  )
}

function CountChip({
  label,
  value,
  tone,
  testId,
}: {
  label: string
  value: number
  tone: 'danger' | 'warn'
  testId: 'sec-count-must_fix' | 'sec-count-when_free'
}) {
  const must = tone === 'danger'
  return (
    <div
      className={cn(
        'rounded-npc-card-sm border px-4 py-3',
        must ? 'sec-count-must border-npc-border' : 'border-npc-amber-border bg-npc-amber-bg',
      )}
      data-testid={testId}
    >
      <div
        className={cn(
          'font-display text-npc-stat-sm font-semibold leading-none',
          must ? 'sec-count-value' : 'text-npc-amber-ink',
        )}
      >
        {value}
      </div>
      <div className="mt-1.5 text-npc-caption-sm text-npc-ink-soft">{label}</div>
    </div>
  )
}

function Group({ title, tone, checks }: { title: string; tone: 'danger' | 'warn'; checks: Check[] }) {
  const must = tone === 'danger'
  return (
    <section className="mb-8">
      <h2 className="mb-3 flex items-center gap-2 text-npc-section-sm font-semibold text-npc-ink">
        <span
          className={cn(
            'inline-block h-1.5 w-1.5 rounded-full',
            must ? 'sec-dot-must' : 'bg-npc-amber',
          )}
          aria-hidden
        />
        {title}
      </h2>
      <ul className="space-y-3">
        {checks.map((c) => (
          <li
            key={c.id}
            data-testid={`sec-check-${c.id}`}
            className="rounded-npc-card border border-npc-border bg-npc-panel px-5 py-4"
          >
            {/* 三句话由后端按 sec.<id>.* 模板渲染好再下发 —— 渲染只发生一处，
                否则同一条结论在接口与界面上会各说各话。 */}
            <h3 className="text-npc-section-sm font-semibold text-npc-ink">{c.title}</h3>
            <p className="mt-2 text-npc-body-sm leading-[1.7] text-npc-ink-medium">{c.explanation}</p>
            {c.suggestion && (
              <p className="mt-2 text-npc-body-sm leading-[1.7] text-npc-ink-soft">{c.suggestion}</p>
            )}
            {c.affected_hosts && c.affected_hosts.length > 0 && (
              <ul className="mt-3 flex flex-wrap gap-1.5">
                {c.affected_hosts.map((h) => (
                  <li
                    key={h.host_id}
                    className="rounded-npc-tag bg-npc-surface px-2 py-1 text-npc-meta text-npc-ink-soft"
                  >
                    {h.name}
                  </li>
                ))}
              </ul>
            )}
          </li>
        ))}
      </ul>
    </section>
  )
}
